import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "../App";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Switch } from "../components/ui/switch";
import {
  Calendar, Leaf, Plus, Trash2, Edit, Sun, Droplets, Sprout,
  ArrowLeft, MapPin, Bell, Mail, Smartphone, Clock, Check,
  ChevronRight, Loader2, Shovel, Apple, Settings,
  CalendarDays, AlertCircle
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

// Use Sprout as Seedling alternative
const Seedling = Sprout;

const PersonalAlmanacPage = () => {
  const navigate = useNavigate();
  const { user, login } = useAuth();
  const [loading, setLoading] = useState(true);
  const [almanacs, setAlmanacs] = useState([]);
  const [limits, setLimits] = useState({ max_almanacs: 1, max_plants: 6 });
  const [selectedAlmanac, setSelectedAlmanac] = useState(null);
  const [calendarEvents, setCalendarEvents] = useState([]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showAddPlantModal, setShowAddPlantModal] = useState(false);
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [newAlmanac, setNewAlmanac] = useState({ name: "My Garden Plan", latitude: null, longitude: null });
  const [newPlant, setNewPlant] = useState({ plant_name: "", variety: "", quantity: 1, notes: "" });
  const [seedStock, setSeedStock] = useState([]);

  const loadAlmanacs = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/almanac`, { withCredentials: true });
      setAlmanacs(response.data.almanacs || []);
      setLimits(response.data.limits || { max_almanacs: 1, max_plants: 6 });
      
      // Auto-select first almanac
      if (response.data.almanacs?.length > 0 && !selectedAlmanac) {
        loadAlmanacDetails(response.data.almanacs[0].id);
      }
    } catch (error) {
      console.error("Failed to load almanacs:", error);
    } finally {
      setLoading(false);
    }
  }, [selectedAlmanac]);

  const loadAlmanacDetails = async (almanacId) => {
    try {
      const [almanacRes, calendarRes] = await Promise.all([
        axios.get(`${API}/almanac/${almanacId}`, { withCredentials: true }),
        axios.get(`${API}/almanac/${almanacId}/calendar`, { withCredentials: true })
      ]);
      setSelectedAlmanac(almanacRes.data);
      setCalendarEvents(calendarRes.data.events || []);
    } catch (error) {
      toast.error("Failed to load almanac details");
    }
  };

  const loadSeedStock = async () => {
    try {
      const response = await axios.get(`${API}/seed-stock`, { withCredentials: true });
      setSeedStock(response.data || []);
    } catch (error) {
      // Seed stock may not be available for all tiers
    }
  };

  useEffect(() => {
    if (user) {
      loadAlmanacs();
      loadSeedStock();
    } else {
      setLoading(false);
    }
  }, [user, loadAlmanacs]);

  const createAlmanac = async () => {
    try {
      // Get user's location if not provided
      if (!newAlmanac.latitude && navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          async (pos) => {
            const response = await axios.post(`${API}/almanac`, {
              ...newAlmanac,
              latitude: pos.coords.latitude,
              longitude: pos.coords.longitude
            }, { withCredentials: true });
            toast.success("Almanac created!");
            setShowCreateModal(false);
            loadAlmanacs();
            loadAlmanacDetails(response.data.almanac.id);
          },
          async () => {
            // Create without location
            const response = await axios.post(`${API}/almanac`, newAlmanac, { withCredentials: true });
            toast.success("Almanac created!");
            setShowCreateModal(false);
            loadAlmanacs();
            loadAlmanacDetails(response.data.almanac.id);
          }
        );
      } else {
        const response = await axios.post(`${API}/almanac`, newAlmanac, { withCredentials: true });
        toast.success("Almanac created!");
        setShowCreateModal(false);
        loadAlmanacs();
        loadAlmanacDetails(response.data.almanac.id);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create almanac");
    }
  };

  const addPlant = async () => {
    if (!selectedAlmanac || !newPlant.plant_name) return;
    
    try {
      await axios.post(`${API}/almanac/${selectedAlmanac.id}/plants`, newPlant, { withCredentials: true });
      toast.success(`${newPlant.plant_name} added to your almanac!`);
      setShowAddPlantModal(false);
      setNewPlant({ plant_name: "", variety: "", quantity: 1, notes: "" });
      loadAlmanacDetails(selectedAlmanac.id);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to add plant");
    }
  };

  const updatePlantStatus = async (plantId, status) => {
    if (!selectedAlmanac) return;
    
    try {
      await axios.patch(`${API}/almanac/${selectedAlmanac.id}/plants/${plantId}`, { status }, { withCredentials: true });
      loadAlmanacDetails(selectedAlmanac.id);
      toast.success("Plant status updated!");
    } catch (error) {
      toast.error("Failed to update plant");
    }
  };

  const removePlant = async (plantId) => {
    if (!selectedAlmanac) return;
    
    try {
      await axios.delete(`${API}/almanac/${selectedAlmanac.id}/plants/${plantId}`, { withCredentials: true });
      loadAlmanacDetails(selectedAlmanac.id);
      toast.success("Plant removed");
    } catch (error) {
      toast.error("Failed to remove plant");
    }
  };

  const updateNotificationPrefs = async (prefs) => {
    if (!selectedAlmanac) return;
    
    try {
      await axios.patch(`${API}/almanac/${selectedAlmanac.id}/notifications`, prefs, { withCredentials: true });
      loadAlmanacDetails(selectedAlmanac.id);
      toast.success("Notification preferences updated!");
    } catch (error) {
      toast.error("Failed to update preferences");
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      planned: "bg-slate-100 text-slate-700",
      started_indoors: "bg-amber-100 text-amber-700",
      transplanted: "bg-green-100 text-green-700",
      growing: "bg-emerald-100 text-emerald-700",
      harvesting: "bg-red-100 text-red-700",
      complete: "bg-purple-100 text-purple-700"
    };
    return colors[status] || colors.planned;
  };

  const getStatusIcon = (status) => {
    const icons = {
      planned: Seedling,
      started_indoors: Sprout,
      transplanted: Shovel,
      growing: Leaf,
      harvesting: Apple,
      complete: Check
    };
    const Icon = icons[status] || Seedling;
    return <Icon className="w-4 h-4" />;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "TBD";
    const date = new Date(dateStr);
    return date.toLocaleDateString("en-US", { month: "short", day: "numeric" });
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-green-50 to-amber-50 flex items-center justify-center p-6">
        <Card className="max-w-md w-full">
          <CardContent className="p-8 text-center">
            <Calendar className="w-16 h-16 mx-auto mb-4 text-green-600" />
            <h2 className="text-xl font-bold mb-2">Personal Garden Almanac</h2>
            <p className="text-muted-foreground mb-6">
              Sign in to create your personalized planting calendar with reminders and schedules based on your zone.
            </p>
            <Button onClick={login} size="lg" data-testid="almanac-login-btn">
              Sign In to Get Started
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-green-50 to-amber-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-green-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-green-50 to-amber-50">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur border-b sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" onClick={() => navigate(-1)} data-testid="back-btn">
              <ArrowLeft className="w-4 h-4 mr-2" /> Back
            </Button>
            <div>
              <h1 className="text-xl font-bold text-green-800 flex items-center gap-2">
                <Calendar className="w-5 h-5" /> Personal Almanac
              </h1>
              <p className="text-sm text-muted-foreground">
                Your personalized planting calendar
              </p>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            <Badge variant="outline" className="text-green-700">
              {almanacs.length}/{limits.max_almanacs} Plans
            </Badge>
            {almanacs.length < limits.max_almanacs && (
              <Button onClick={() => setShowCreateModal(true)} data-testid="create-almanac-btn">
                <Plus className="w-4 h-4 mr-2" /> New Plan
              </Button>
            )}
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        {almanacs.length === 0 ? (
          /* Empty State */
          <Card className="max-w-lg mx-auto">
            <CardContent className="p-12 text-center">
              <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <Seedling className="w-10 h-10 text-green-600" />
              </div>
              <h2 className="text-2xl font-bold mb-3">Start Your Garden Journey</h2>
              <p className="text-muted-foreground mb-6">
                Create your first personal almanac to get customized planting schedules, 
                reminders, and harvest timelines based on your location.
              </p>
              <Button onClick={() => setShowCreateModal(true)} size="lg" data-testid="create-first-almanac-btn">
                <Plus className="w-5 h-5 mr-2" /> Create Your Almanac
              </Button>
              <p className="text-xs text-muted-foreground mt-4">
                Free: {limits.max_almanacs} plan, {limits.max_plants} plants
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid lg:grid-cols-3 gap-6">
            {/* Almanac Selector & Calendar Events */}
            <div className="lg:col-span-1 space-y-4">
              {/* Almanac List */}
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium flex items-center gap-2">
                    <CalendarDays className="w-4 h-4" /> Your Plans
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  {almanacs.map(almanac => (
                    <button
                      key={almanac.id}
                      onClick={() => loadAlmanacDetails(almanac.id)}
                      className={`w-full text-left p-3 rounded-lg border transition-all ${
                        selectedAlmanac?.id === almanac.id 
                          ? 'border-green-500 bg-green-50' 
                          : 'border-transparent hover:bg-slate-50'
                      }`}
                      data-testid={`almanac-select-${almanac.id}`}
                    >
                      <div className="flex items-center justify-between">
                        <span className="font-medium">{almanac.name}</span>
                        <ChevronRight className="w-4 h-4 text-muted-foreground" />
                      </div>
                      <div className="text-xs text-muted-foreground mt-1 flex items-center gap-2">
                        {almanac.zone && (
                          <span className="flex items-center gap-1">
                            <MapPin className="w-3 h-3" /> Zone {almanac.zone}
                          </span>
                        )}
                        <span>{almanac.plants?.length || 0} plants</span>
                      </div>
                    </button>
                  ))}
                </CardContent>
              </Card>

              {/* Upcoming Events */}
              {calendarEvents.length > 0 && (
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium flex items-center gap-2">
                      <Bell className="w-4 h-4" /> Upcoming Tasks
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2 max-h-80 overflow-y-auto">
                    {calendarEvents.slice(0, 10).map(event => (
                      <div 
                        key={event.id}
                        className="p-2 rounded border-l-4 bg-slate-50"
                        style={{ borderLeftColor: event.color }}
                      >
                        <p className="text-sm font-medium">{event.title}</p>
                        <p className="text-xs text-muted-foreground">{formatDate(event.date)}</p>
                      </div>
                    ))}
                  </CardContent>
                </Card>
              )}
            </div>

            {/* Main Content */}
            <div className="lg:col-span-2 space-y-4">
              {selectedAlmanac ? (
                <>
                  {/* Almanac Header */}
                  <Card>
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <h2 className="text-xl font-bold">{selectedAlmanac.name}</h2>
                          <div className="flex items-center gap-4 text-sm text-muted-foreground mt-1">
                            {selectedAlmanac.zone && (
                              <span className="flex items-center gap-1">
                                <Sun className="w-4 h-4 text-amber-500" /> Zone {selectedAlmanac.zone}
                              </span>
                            )}
                            {selectedAlmanac.last_frost_date && (
                              <span>Last Frost: {selectedAlmanac.last_frost_date}</span>
                            )}
                            {selectedAlmanac.first_frost_date && (
                              <span>First Frost: {selectedAlmanac.first_frost_date}</span>
                            )}
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <Button variant="outline" size="sm" onClick={() => setShowSettingsModal(true)}>
                            <Settings className="w-4 h-4" />
                          </Button>
                          {selectedAlmanac.plants?.length < limits.max_plants && (
                            <Button size="sm" onClick={() => setShowAddPlantModal(true)} data-testid="add-plant-btn">
                              <Plus className="w-4 h-4 mr-1" /> Add Plant
                            </Button>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Plants Grid */}
                  <div className="grid sm:grid-cols-2 gap-4">
                    {selectedAlmanac.plants?.map(plant => (
                      <Card key={plant.id} className="overflow-hidden">
                        <CardContent className="p-4">
                          <div className="flex items-start justify-between mb-3">
                            <div>
                              <h3 className="font-semibold">{plant.plant_name}</h3>
                              {plant.variety && (
                                <p className="text-sm text-muted-foreground">{plant.variety}</p>
                              )}
                            </div>
                            <Badge className={getStatusColor(plant.status)}>
                              {getStatusIcon(plant.status)}
                              <span className="ml-1 capitalize">{plant.status.replace("_", " ")}</span>
                            </Badge>
                          </div>
                          
                          {/* Schedule Timeline */}
                          <div className="space-y-2 text-sm">
                            {plant.start_indoors_date && (
                              <div className="flex items-center gap-2">
                                <Sprout className="w-4 h-4 text-amber-500" />
                                <span>Start indoors: {formatDate(plant.start_indoors_date)}</span>
                              </div>
                            )}
                            {plant.transplant_date && (
                              <div className="flex items-center gap-2">
                                <Shovel className="w-4 h-4 text-green-500" />
                                <span>Transplant: {formatDate(plant.transplant_date)}</span>
                              </div>
                            )}
                            {plant.direct_sow_date && (
                              <div className="flex items-center gap-2">
                                <Seedling className="w-4 h-4 text-blue-500" />
                                <span>Direct sow: {formatDate(plant.direct_sow_date)}</span>
                              </div>
                            )}
                            {plant.harvest_start_date && (
                              <div className="flex items-center gap-2">
                                <Apple className="w-4 h-4 text-red-500" />
                                <span>Harvest: {formatDate(plant.harvest_start_date)}</span>
                              </div>
                            )}
                          </div>
                          
                          {/* Actions */}
                          <div className="flex items-center justify-between mt-4 pt-3 border-t">
                            <select
                              value={plant.status}
                              onChange={(e) => updatePlantStatus(plant.id, e.target.value)}
                              className="text-sm border rounded px-2 py-1"
                            >
                              <option value="planned">Planned</option>
                              <option value="started_indoors">Started Indoors</option>
                              <option value="transplanted">Transplanted</option>
                              <option value="growing">Growing</option>
                              <option value="harvesting">Harvesting</option>
                              <option value="complete">Complete</option>
                            </select>
                            <Button 
                              variant="ghost" 
                              size="sm" 
                              onClick={() => removePlant(plant.id)}
                              className="text-red-500 hover:text-red-700"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                    
                    {selectedAlmanac.plants?.length === 0 && (
                      <Card className="col-span-full">
                        <CardContent className="p-8 text-center">
                          <Seedling className="w-12 h-12 mx-auto mb-3 text-muted-foreground opacity-50" />
                          <p className="text-muted-foreground">No plants yet. Add your first plant to get started!</p>
                        </CardContent>
                      </Card>
                    )}
                  </div>
                </>
              ) : (
                <Card>
                  <CardContent className="p-8 text-center">
                    <p className="text-muted-foreground">Select an almanac to view details</p>
                  </CardContent>
                </Card>
              )}
            </div>
          </div>
        )}
      </main>

      {/* Create Almanac Modal */}
      <AnimatePresence>
        {showCreateModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50"
            onClick={() => setShowCreateModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0.95 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white rounded-xl p-6 max-w-md w-full"
            >
              <h3 className="text-lg font-bold mb-4">Create New Almanac</h3>
              <div className="space-y-4">
                <div>
                  <Label>Plan Name</Label>
                  <Input
                    value={newAlmanac.name}
                    onChange={(e) => setNewAlmanac({ ...newAlmanac, name: e.target.value })}
                    placeholder="My Garden Plan"
                    data-testid="almanac-name-input"
                  />
                </div>
                <p className="text-sm text-muted-foreground">
                  We&apos;ll use your location to calculate your growing zone and frost dates automatically.
                </p>
                <div className="flex gap-2 justify-end">
                  <Button variant="outline" onClick={() => setShowCreateModal(false)}>Cancel</Button>
                  <Button onClick={createAlmanac} data-testid="create-almanac-submit">Create</Button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Add Plant Modal */}
      <AnimatePresence>
        {showAddPlantModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50"
            onClick={() => setShowAddPlantModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0.95 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white rounded-xl p-6 max-w-md w-full max-h-[80vh] overflow-y-auto"
            >
              <h3 className="text-lg font-bold mb-4">Add Plant to Almanac</h3>
              <div className="space-y-4">
                {/* Quick add from seed stock */}
                {seedStock.length > 0 && (
                  <div>
                    <Label className="mb-2 block">From Your Seed Stock</Label>
                    <div className="flex flex-wrap gap-2">
                      {seedStock.slice(0, 6).map(seed => (
                        <button
                          key={seed.id}
                          onClick={() => setNewPlant({ 
                            ...newPlant, 
                            plant_name: seed.plant_name, 
                            variety: seed.variety,
                            seed_stock_id: seed.id 
                          })}
                          className={`px-3 py-1 rounded-full text-sm border transition-all ${
                            newPlant.seed_stock_id === seed.id 
                              ? 'bg-green-100 border-green-500' 
                              : 'hover:bg-slate-50'
                          }`}
                        >
                          {seed.plant_name}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
                
                <div>
                  <Label>Plant Name *</Label>
                  <Input
                    value={newPlant.plant_name}
                    onChange={(e) => setNewPlant({ ...newPlant, plant_name: e.target.value })}
                    placeholder="Tomato, Pepper, Basil..."
                    data-testid="plant-name-input"
                  />
                </div>
                <div>
                  <Label>Variety (optional)</Label>
                  <Input
                    value={newPlant.variety}
                    onChange={(e) => setNewPlant({ ...newPlant, variety: e.target.value })}
                    placeholder="Roma, Cherokee Purple..."
                  />
                </div>
                <div>
                  <Label>Quantity</Label>
                  <Input
                    type="number"
                    min="1"
                    value={newPlant.quantity}
                    onChange={(e) => setNewPlant({ ...newPlant, quantity: parseInt(e.target.value) || 1 })}
                  />
                </div>
                <div>
                  <Label>Notes (optional)</Label>
                  <Input
                    value={newPlant.notes}
                    onChange={(e) => setNewPlant({ ...newPlant, notes: e.target.value })}
                    placeholder="Companion plant with basil..."
                  />
                </div>
                <div className="flex gap-2 justify-end">
                  <Button variant="outline" onClick={() => setShowAddPlantModal(false)}>Cancel</Button>
                  <Button onClick={addPlant} disabled={!newPlant.plant_name} data-testid="add-plant-submit">
                    Add Plant
                  </Button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Settings Modal */}
      <AnimatePresence>
        {showSettingsModal && selectedAlmanac && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50"
            onClick={() => setShowSettingsModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0.95 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white rounded-xl p-6 max-w-md w-full"
            >
              <h3 className="text-lg font-bold mb-4">Notification Settings</h3>
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Mail className="w-4 h-4" />
                    <span>Email Reminders</span>
                  </div>
                  <Switch
                    checked={selectedAlmanac.notification_prefs?.email}
                    onCheckedChange={(checked) => updateNotificationPrefs({ email: checked })}
                  />
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Bell className="w-4 h-4" />
                    <span>Push Notifications</span>
                  </div>
                  <Switch
                    checked={selectedAlmanac.notification_prefs?.push}
                    onCheckedChange={(checked) => updateNotificationPrefs({ push: checked })}
                  />
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Smartphone className="w-4 h-4" />
                    <span>SMS Reminders</span>
                  </div>
                  <Switch
                    checked={selectedAlmanac.notification_prefs?.sms}
                    onCheckedChange={(checked) => updateNotificationPrefs({ sms: checked })}
                  />
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Droplets className="w-4 h-4" />
                    <span>Watering Reminders</span>
                  </div>
                  <Switch
                    checked={selectedAlmanac.notification_prefs?.watering_reminders}
                    onCheckedChange={(checked) => updateNotificationPrefs({ watering_reminders: checked })}
                  />
                </div>
                <div>
                  <Label>Remind me this many days before tasks</Label>
                  <Input
                    type="number"
                    min="1"
                    max="14"
                    value={selectedAlmanac.notification_prefs?.reminder_days_before || 3}
                    onChange={(e) => updateNotificationPrefs({ reminder_days_before: parseInt(e.target.value) })}
                  />
                </div>
                <Button className="w-full" onClick={() => setShowSettingsModal(false)}>Done</Button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default PersonalAlmanacPage;
